import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from "@mui/material";
import { ChangeEvent, useEffect, useState } from 'react';
import { ExternalConnectionModel, UpsertExternalConnectionInput } from '../../gql-types.generated';
import AddEditDialog from "./AddEditDialog";

interface ExternalConnectionDialogProps {
    isOpen: boolean;
    externalConnection?: ExternalConnectionModel | null | undefined;
    onClose: () => void;
    onSave: (upsertExternalConnectionData: UpsertExternalConnectionInput) => void;
    error?: Error | undefined;
}

const ExternalConnectionDialog: React.FC<ExternalConnectionDialogProps> = props => {
    const { isOpen, externalConnection, onClose, onSave, error } = props;
    const [isFormDirty, setIsFormDirty] = useState(false); // Dirty state of the form.
    const [id, setId] = useState<string>(''); 
    const [externalConnectionType, setExternalConnectionType] = useState<string>(''); 
    const [description, setDescription] = useState<string>('');
    const [isForClient, setIsForClient] = useState<boolean>(false);
    const [isForPartner, setIsForPartner] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState(false); // Submitted state of the form
    
    useEffect(() => {
        // if have an external connection, then populate for Edit
        if (externalConnection){
            setFromExternalConnection();
        }
        else {
            setToDefaults();
        }
    },[externalConnection]);

    useEffect(() => {
        // set submitted to false and clear fields when dialog is closed
        if (!isOpen) {
            setToDefaults();
            setSubmitted(false);
            setIsFormDirty(false);
        }
        else {
            setFromExternalConnection();
        }
    }, [isOpen]);

    const setToDefaults = () => {
        setId('');
        setExternalConnectionType('');
        setDescription('');
        setIsForClient(false);
        setIsForPartner(false);
    }

    const setFromExternalConnection = () => {
        if (externalConnection){
            if (externalConnection.externalConnectionType) {
                // need to set a separate Id field for indication of add vs edit
                setId(externalConnection.externalConnectionType);
                setExternalConnectionType(externalConnection.externalConnectionType);
            }

            if (externalConnection.description) {
                setDescription(externalConnection.description);
            }
            setIsForClient(externalConnection.isForClient);
            setIsForPartner(externalConnection.isForPartner);
        }
    }

    const onError = () => {
        setSubmitted(false);
    }

    const isFormValid = () => {
        //at least one of isForClient or isForPartner must be true
        let isValidClientPartner = isForClient || isForPartner;
        return isFormDirty && isValidClientPartner && externalConnectionType?.trim().length > 0;
    };
    const submitForm = () => {
        setSubmitted(true);
        onSave({ externalConnectionType, description: description || undefined, isForClient: isForClient, isForPartner: isForPartner, isForAdd: !id });
    };

    // onChange handlers
    const onTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setExternalConnectionType(event.target.value);
    };
    const onDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setDescription(event.target.value);
    };
    const onIsForClientChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setIsForClient(event.target.checked);
    };
    const onIsForPartnerChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setIsForPartner(event.target.checked);
    };

    const typeProps = {
        'aria-label': 'external connection type',
        'maxLength': 10,
    };
    const descriptionProps = {
        'aria-label': 'description',
        'maxLength': 255,
    };

    // form
    return (
        <AddEditDialog 
            isOpen={isOpen} 
            isSubmitted={submitted}
            id={id} 
            entityName="External Connection" 
            onClose={onClose} 
            onSave={submitForm} 
            validate={isFormValid}
            onError={onError}
            error={error}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-external-connection-type"
                        fullWidth
                        disabled={submitted || id?.length > 0} // disable if editing
                        autoFocus={id?.length === 0} // autofocus if adding
                        value={externalConnectionType}
                        label="External Connection Type"
                        inputProps={typeProps}
                        onChange={onTypeChange}
                        autoComplete="off"
                        required
                        data-cy="dialog-external-connection-type"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        itemID="dialog-external-connection-description"
                        fullWidth
                        disabled={submitted}
                        value={description}
                        label="Description"
                        inputProps={descriptionProps}
                        onChange={onDescriptionChange}
                        autoComplete="off"
                        data-cy="dialog-external-connection-description"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ mt: 1.5 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            Must select at least one:
                        </Typography>
                        <Box sx={{ ml: 1 }}>
                            <FormControlLabel 
                                control={
                                    <Checkbox
                                        checked={isForClient}
                                        title="Is for Client"
                                        onChange={onIsForClientChange}
                                        color="primary"
                                        inputProps={{ 'aria-label': 'is for client' }}
                                    />
                                } 
                                label="Is For Client" 
                                sx={{ display: 'block', mb: 0.5 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isForPartner}
                                        title="Is for Partner"
                                        onChange={onIsForPartnerChange}
                                        color="primary"
                                        inputProps={{ 'aria-label': 'is for partner' }}
                                    />
                                }
                                label="Is For Partner"
                                sx={{ display: 'block' }}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </AddEditDialog>
    );
};

export default ExternalConnectionDialog;
